/**
 * Proto → GeoJSON, one pure builder per mark type. No Mapbox, no DOM:
 * everything a layer draws is decided here and unit-tested here.
 *
 * Every feature carries a string `id` (the sources use promoteId: 'id' for
 * feature-state hover/selection) and only the properties its layer needs.
 * In particular `target_player_id` from police/roamers is NEVER copied: who a
 * cop is chasing is another player's position by proxy.
 */
import { BuildingStatus, type BuildingInfo } from '@/gen/building/v1/building_pb'
import type { NPCView } from '@/gen/npc/v1/npc_pb'
import type { Npc } from '@/gen/police/v1/police_pb'
import { Hostility, type Roamer } from '@/gen/roamer/v1/roamer_pb'
import { CrewRelation, type CrewMarker, type HeatCell } from '@/gen/webgame/v1/crew_map_pb'
import { SOC, type SocToken } from '@/styles/tokens'
import { meshForBuilding, type BuildingMesh } from './buildingAsset'
import { labelKey, markKey } from './marks'
import { metersToPixels, type GeoBounds } from './viewport'

// ---------------------------------------------------------------------------
// GeoJSON shapes (structurally compatible with @types/geojson)
// ---------------------------------------------------------------------------

export interface PointGeometry {
  type: 'Point'
  coordinates: [number, number]
}
export interface PolygonGeometry {
  type: 'Polygon'
  coordinates: [number, number][][]
}
export interface Feature<G, P> {
  type: 'Feature'
  id: string
  geometry: G
  properties: P
}
export interface FeatureCollection<G, P> {
  type: 'FeatureCollection'
  features: Feature<G, P>[]
}

export const emptyCollection = <G, P>(): FeatureCollection<G, P> => ({ type: 'FeatureCollection', features: [] })

/** (0,0) is the null island: an unpopulated row, not a building in the Gulf of Guinea. */
function hasPosition(lat: number | undefined, lng: number | undefined): lat is number {
  return typeof lat === 'number' && typeof lng === 'number' && Number.isFinite(lat) && Number.isFinite(lng) && !(lat === 0 && lng === 0)
}

function point<P>(id: string, lat: number, lng: number, properties: P): Feature<PointGeometry, P> {
  return { type: 'Feature', id, geometry: { type: 'Point', coordinates: [lng, lat] }, properties }
}

// ---------------------------------------------------------------------------
// Factions
// ---------------------------------------------------------------------------

export type FactionKey = 'italian' | 'yakuza' | 'cartel' | 'triad' | 'neutral'

/**
 * Faction code → canonical key. "irish" and "triad" are ONE faction under two
 * names (the Unity enum offers irish where the token is --soc-fx-triad), so
 * both resolve to triad's slate. Anything unknown is neutral, never a guess.
 */
export function normalizeFaction(faction: string | undefined | null): FactionKey {
  switch ((faction ?? '').trim().toLowerCase()) {
    case 'italian':
      return 'italian'
    case 'yakuza':
      return 'yakuza'
    case 'cartel':
      return 'cartel'
    case 'triad':
    case 'irish':
      return 'triad'
    default:
      return 'neutral'
  }
}

export function factionToken(faction: string | undefined | null): SocToken {
  const k = normalizeFaction(faction)
  return `fx-${k}` as SocToken
}

// ---------------------------------------------------------------------------
// Buildings
// ---------------------------------------------------------------------------

export type BuildingRel = 'mine' | 'faction' | 'rival' | 'unclaimed'
export type BuildingStatusKey = 'unclaimed' | 'claimed' | 'destroyed' | 'challenged'

export interface BuildingProps {
  id: string
  name: string
  status: BuildingStatusKey
  faction: FactionKey
  rel: BuildingRel
  quest: boolean
  /**
   * Which 3D mesh `wg-buildings-model` draws, resolved in TypeScript rather
   * than as a `match` expression in the style. A model-id the style does not
   * declare draws NOTHING — the building vanishes with only a 404 — so the
   * fallback belongs somewhere a test can reach it. Chosen by hashing the
   * building's own id, so it is the same mesh on every reload and for every
   * viewer.
   */
  asset: BuildingMesh
  /**
   * Resolved colour for `model-color`, which takes a value and cannot resolve
   * a token. Follows the same priority the square's stroke does, so the model
   * and the mark never disagree about what a building is.
   */
  tint: string
  /**
   * A small mark drawn ABOVE the 3D model, carrying the two things the mesh
   * cannot: "mine" and "has work". The square encodes those as an ivory stroke
   * and a brass notch, and the square is hidden at model zoom — without this,
   * switching to 3D silently costs a player the ability to tell their own
   * block from an ally's, or to see an offer at a glance.
   *
   * ABSENT when a building is neither, so `['get', 'badge']` resolves to null
   * and nothing is drawn — the same contract as `label`.
   */
  badge?: string
  icon: string
  /**
   * Rasterised name, for `wg-buildings-label`. ABSENT when the building has no
   * name: `['get', 'label']` then evaluates to null and the symbol is simply
   * not drawn, where an empty string would be asked for as an image and warn
   * once per building.
   */
  label?: string
  /** symbol-sort-key: higher draws on top. */
  sort: number
}

export const BUILDING_SIZE = 12
export const BUILDING_SIZE_MINE = 14

export function statusKey(status: BuildingStatus): BuildingStatusKey {
  switch (status) {
    case BuildingStatus.CLAIMED:
      return 'claimed'
    case BuildingStatus.DESTROYED:
      return 'destroyed'
    case BuildingStatus.CHALLENGED:
      return 'challenged'
    default:
      // UNSPECIFIED reads as unclaimed ON PURPOSE: it means "the server has
      // not said", and the honest way to draw silence is the unclaimed way.
      return 'unclaimed'
  }
}

/** Owned = has an owner, or a status that implies one. */
export function isOwned(b: Pick<BuildingInfo, 'ownerPlayerId' | 'status'>): boolean {
  return Boolean(b.ownerPlayerId) || b.status === BuildingStatus.CLAIMED || b.status === BuildingStatus.CHALLENGED
}

export function buildingRel(b: BuildingInfo, selfId: string | null, selfFaction: string | null): BuildingRel {
  if (selfId && b.ownerPlayerId === selfId) return 'mine'
  if (!isOwned(b)) return 'unclaimed'
  return normalizeFaction(b.ownerFaction) === normalizeFaction(selfFaction) && normalizeFaction(selfFaction) !== 'neutral'
    ? 'faction'
    : 'rival'
}

/**
 * The building icon. Exactly one stroke, in priority order: destroyed and
 * challenged outrank "mine" because they are time-boxed (a rebuild clock, a
 * challenge deadline) while ownership is a standing fact.
 */
export function buildingIcon(b: BuildingInfo, rel: BuildingRel): string {
  const status = statusKey(b.status)
  const mine = rel === 'mine'
  const size = mine ? BUILDING_SIZE_MINE : BUILDING_SIZE
  const owned = isOwned(b)
  const fill: SocToken = owned ? factionToken(b.ownerFaction) : 'fx-neutral'
  const notch = b.hasQuestsAvailable
  if (status === 'destroyed') {
    return markKey({ shape: 'sq', size, fill: 'bg-deep', fillAlpha: 200, stroke: 'danger', strokeWidth: 1.5, cross: true, notch })
  }
  if (status === 'challenged') {
    return markKey({ shape: 'sq', size, fill, fillAlpha: 255, stroke: 'warning', strokeWidth: 2, ring: true, notch })
  }
  if (mine) {
    // The only ivory stroke on the map: "that one is mine" without a legend.
    return markKey({ shape: 'sq', size, fill, fillAlpha: 255, stroke: 'ink', strokeWidth: 2, notch })
  }
  // Unowned buildings recede (alpha), they do not vanish.
  return markKey({ shape: 'sq', size, fill, fillAlpha: owned ? 255 : 170, stroke: 'outline', strokeWidth: 1, notch })
}

/**
 * The colour washed over the 3D model. Status outranks ownership for the same
 * reason it does on the square: a rebuild clock and a challenge deadline are
 * time-boxed, and ownership is a standing fact that can wait.
 */
export function buildingTint(b: BuildingInfo, status: BuildingStatusKey): string {
  if (status === 'destroyed') return SOC.danger
  if (status === 'challenged') return SOC.warning
  return SOC[isOwned(b) ? factionToken(b.ownerFaction) : 'fx-neutral']
}

/**
 * The badge above a 3D model: a small square in the same grammar as the mark
 * it stands in for, stroked ivory for "mine" and notched brass for an offer.
 * Undefined when there is nothing to say.
 */
export function buildingBadge(rel: BuildingRel, quest: boolean): string | undefined {
  const mine = rel === 'mine'
  if (!mine && !quest) return undefined
  return markKey({
    shape: 'sq',
    size: 9,
    // A little body so it reads against a lit mesh rather than only against
    // the ground.
    fill: 'bg-deep',
    fillAlpha: 150,
    stroke: mine ? 'ink' : 'brass',
    strokeWidth: 2,
    notch: quest,
  })
}

function buildingSort(status: BuildingStatusKey, rel: BuildingRel): number {
  if (rel === 'mine') return 4
  if (status === 'challenged') return 3
  if (status === 'destroyed') return 2
  return rel === 'unclaimed' ? 0 : 1
}

export function buildingFeatures(
  buildings: readonly BuildingInfo[],
  selfId: string | null,
  selfFaction: string | null,
): FeatureCollection<PointGeometry, BuildingProps> {
  const features: Feature<PointGeometry, BuildingProps>[] = []
  const seen = new Set<string>()
  for (const b of buildings) {
    if (!b.id || seen.has(b.id) || !hasPosition(b.latitude, b.longitude)) continue
    seen.add(b.id)
    const rel = buildingRel(b, selfId, selfFaction)
    const status = statusKey(b.status)
    const badge = buildingBadge(rel, b.hasQuestsAvailable)
    features.push(
      point(b.id, b.latitude, b.longitude, {
        id: b.id,
        name: b.name,
        status,
        faction: isOwned(b) ? normalizeFaction(b.ownerFaction) : 'neutral',
        rel,
        quest: b.hasQuestsAvailable,
        asset: meshForBuilding(b.gameType, b.id),
        tint: buildingTint(b, status),
        ...(badge ? { badge } : {}),
        icon: buildingIcon(b, rel),
        // A named establishment is a proper noun, so DM Serif — the same call
        // the quest-giver labels make. Ink, never faction colour: the square
        // underneath already carries ownership, and saying it twice is what
        // the recon map learned not to do.
        ...(b.name ? { label: labelKey(b.name, 'ink-soft', 'serif') } : {}),
        sort: buildingSort(status, rel),
      }),
    )
  }
  return { type: 'FeatureCollection', features }
}

// ---------------------------------------------------------------------------
// Quest givers
// ---------------------------------------------------------------------------

export interface GiverProps {
  id: string
  name: string
  quests: boolean
  color: string
  label: string
}

export function giverFeatures(npcs: readonly NPCView[]): FeatureCollection<PointGeometry, GiverProps> {
  const features: Feature<PointGeometry, GiverProps>[] = []
  for (const n of npcs) {
    if (!n.id || !n.enabled || !hasPosition(n.lat, n.lng)) continue
    const token: SocToken = n.hasQuestsAvailable ? 'brass' : 'brass-dim'
    features.push(
      point(n.id, n.lat, n.lng as number, {
        id: n.id,
        name: n.name,
        quests: n.hasQuestsAvailable,
        color: SOC[token],
        label: labelKey(n.name, 'brass', 'serif'),
      }),
    )
  }
  return { type: 'FeatureCollection', features }
}

// ---------------------------------------------------------------------------
// Police
// ---------------------------------------------------------------------------

export interface PoliceProps {
  id: string
  /** "guardian" | "responder" — never shown raw, only used to pick copy. */
  kind: string
  state: string
  hunting: boolean
  icon: string
  /** Attack range in px at zoom 0; the ring layer scales it by 2^zoom. */
  rangePxZ0: number
}

/** Only pursuit and engage are hunting: a map where everything is dangerous says nothing is. */
export function isHunting(state: string): boolean {
  return state === 'pursuit' || state === 'engage'
}

/**
 * Police ink by state. DELIBERATELY NOT BLUE: --soc-info is the self marker,
 * --soc-fx-triad is a faction, and mid-blue is the worst legibility case this
 * basemap has — its night water is #3e6597, which a blue diamond disappears
 * into. An unknown state lands on the NEUTRAL end of the ramp, never red.
 */
export function policeInk(state: string): SocToken {
  switch (state) {
    case 'alert':
      return 'warning'
    case 'pursuit':
    case 'engage':
      return 'danger'
    case 'lost':
    case 'return':
      return 'ink-mute'
    default:
      return 'ink-soft'
  }
}

export const POLICE_SIZE = 14

export function policeFeatures(npcs: readonly Npc[]): FeatureCollection<PointGeometry, PoliceProps> {
  const features: Feature<PointGeometry, PoliceProps>[] = []
  for (const n of npcs) {
    if (!n.id || !hasPosition(n.lat, n.lng)) continue
    // Explicit allowlist of properties: target_player_id is never copied.
    features.push(
      point(n.id, n.lat, n.lng, {
        id: n.id,
        kind: n.type,
        state: n.state,
        hunting: isHunting(n.state),
        icon: markKey({ shape: 'dm', size: POLICE_SIZE, fill: policeInk(n.state), fillAlpha: 255, stroke: 'outline', strokeWidth: 1 }),
        rangePxZ0: n.attackRangeM > 0 ? metersToPixels(n.attackRangeM, n.lat, 0) : 0,
      }),
    )
  }
  return { type: 'FeatureCollection', features }
}

// ---------------------------------------------------------------------------
// Roamers
// ---------------------------------------------------------------------------

export interface RoamerProps {
  id: string
  name: string
  archetype: string
  state: string
  hostile: boolean
}

export function roamerFeatures(roamers: readonly Roamer[]): FeatureCollection<PointGeometry, RoamerProps> {
  const features: Feature<PointGeometry, RoamerProps>[] = []
  for (const r of roamers) {
    if (!r.id || !hasPosition(r.lat, r.lng)) continue
    features.push(
      point(r.id, r.lat, r.lng, {
        id: r.id,
        name: r.displayName,
        archetype: r.archetypeKey,
        state: r.state,
        hostile: r.hostility === Hostility.AGGRESSIVE || isHunting(r.state),
      }),
    )
  }
  return { type: 'FeatureCollection', features }
}

// ---------------------------------------------------------------------------
// Crew
// ---------------------------------------------------------------------------

export type CrewRelationKey = 'self' | 'family' | 'friend'

export interface CrewProps {
  id: string
  relation: CrewRelationKey
  nickname: string
  color: string
  stale: boolean
  downed: boolean
  layLow: boolean
  label: string
  /** Draw order: self on top. */
  sort: number
}

/** A crew position older than this fades: they may have stopped playing. */
export const CREW_STALE_MS = 60_000

export function crewRelationKey(r: CrewRelation): CrewRelationKey | null {
  switch (r) {
    case CrewRelation.SELF:
      return 'self'
    case CrewRelation.FAMILY:
      return 'family'
    case CrewRelation.FRIEND:
      return 'friend'
    default:
      return null
  }
}

/** --soc-info is reserved for the player's own position marker. */
export function crewToken(relation: CrewRelationKey): SocToken {
  switch (relation) {
    case 'self':
      return 'info'
    case 'family':
      return 'success'
    case 'friend':
      return 'chrome'
  }
}

export function crewFeatures(crew: readonly CrewMarker[], serverNowMs: number): FeatureCollection<PointGeometry, CrewProps> {
  const features: Feature<PointGeometry, CrewProps>[] = []
  for (const c of crew) {
    const relation = crewRelationKey(c.relation)
    if (!c.playerId || !relation || !hasPosition(c.lat, c.lng)) continue
    const ts = Number(c.tsUnixMs)
    const token = crewToken(relation)
    features.push(
      point(c.playerId, c.lat, c.lng, {
        id: c.playerId,
        relation,
        nickname: c.nickname,
        color: SOC[token],
        stale: serverNowMs > 0 && ts > 0 && serverNowMs - ts > CREW_STALE_MS,
        downed: c.downed,
        layLow: Number(c.layLowUntilUnixMs) > serverNowMs,
        label: labelKey(c.nickname || '—', token, 'serif'),
        sort: relation === 'self' ? 2 : relation === 'family' ? 1 : 0,
      }),
    )
  }
  return { type: 'FeatureCollection', features }
}

// ---------------------------------------------------------------------------
// Rival heat
// ---------------------------------------------------------------------------

export interface HeatProps {
  id: string
  faction: FactionKey
  band: number
  color: string
  opacity: number
}

/** Band 1..5 → fill opacity. Banded on purpose: never an exact count. */
export function heatOpacity(band: number): number {
  const b = Math.max(1, Math.min(5, Math.round(band)))
  return Number((0.06 + 0.05 * b).toFixed(2))
}

/**
 * The cell's ink: the faction with the highest band (ties → first listed),
 * or neutral when no single faction clears the threshold on its own.
 */
export function dominantFaction(cell: HeatCell): FactionKey {
  let best: { faction: string; band: number } | null = null
  for (const f of cell.factions) {
    if (!best || f.band > best.band) best = { faction: f.faction, band: f.band }
  }
  return best ? normalizeFaction(best.faction) : 'neutral'
}

export function heatFeatures(cells: readonly HeatCell[]): FeatureCollection<PolygonGeometry, HeatProps> {
  const features: Feature<PolygonGeometry, HeatProps>[] = []
  for (const cell of cells) {
    if (!cell.s2Token || cell.vertices.length < 3 || cell.totalBand <= 0) continue
    const ring: [number, number][] = cell.vertices.map((v) => [v.lng, v.lat])
    const [fx, fy] = ring[0]
    const [lx, ly] = ring[ring.length - 1]
    if (fx !== lx || fy !== ly) ring.push([fx, fy])
    const faction = dominantFaction(cell)
    features.push({
      type: 'Feature',
      id: cell.s2Token,
      geometry: { type: 'Polygon', coordinates: [ring] },
      properties: {
        id: cell.s2Token,
        faction,
        band: cell.totalBand,
        color: SOC[`fx-${faction}` as SocToken],
        opacity: heatOpacity(cell.totalBand),
      },
    })
  }
  return { type: 'FeatureCollection', features }
}

// ---------------------------------------------------------------------------
// Coverage
// ---------------------------------------------------------------------------

/** The hairline around what was actually fetched: panning past the data is DRAWN, never faked. */
export function coverageFeatures(bounds: GeoBounds | null): FeatureCollection<PolygonGeometry, { id: string }> {
  if (!bounds) return emptyCollection()
  const { minLat, minLng, maxLat, maxLng } = bounds
  return {
    type: 'FeatureCollection',
    features: [
      {
        type: 'Feature',
        id: 'coverage',
        geometry: {
          type: 'Polygon',
          coordinates: [
            [
              [minLng, minLat],
              [maxLng, minLat],
              [maxLng, maxLat],
              [minLng, maxLat],
              [minLng, minLat],
            ],
          ],
        },
        properties: { id: 'coverage' },
      },
    ],
  }
}
